import mixpanelBrowser, { Config, Mixpanel } from "mixpanel-browser"

import { toLowerCase } from "@/helpers/string.helpers"
import { AbstractTransport } from "./Transport"
import { isExternalUser, isSameEmail } from "./utils"

import type { Services } from "@/interfaces/services"

class MixpanelTransport extends AbstractTransport {
  protected instance: undefined | Mixpanel
  protected requestIP: null | Promise<string> = null
  protected identified: boolean = false
  protected defaultMetaAttributes: Record<string, unknown> = {}

  constructor(
    token: string,
    mixpanelConfig: Partial<Config> & { record_heatmap_data?: boolean }, // TypeScript compatibility before v2.64.0
    params: { level: Services.LoggerAdapter.LevelKeys },
    instance: Mixpanel = mixpanelBrowser
  ) {
    super(params)

    if (token) {
      mixpanelBrowser.init(token, mixpanelConfig)
      this.instance = instance
    }

    this.requestIP = this.getUserIP()
  }

  private async getUserIP() {
    try {
      const response = await fetch("https://api.ipify.org?format=json")
      const { ip } = await response.json()

      return ip
    } catch {
      return null
    }
  }

  private stopSessionRecording() {
    try {
      if (this.identified) this.instance?.stop_session_recording()
    } catch {
      return
    }
  }

  public setDefaultMetaAttributes(attributes: Record<string, string>) {
    this.defaultMetaAttributes = {
      ...this.defaultMetaAttributes,
      ...attributes
    }
  }

  public identify(data: Services.LoggerAdapter.IdentificationData) {
    // Mixpanel keeps the identified email across page loads.
    // Another person on this browser starts clean, so two people are never merged.
    const identifiedEmail: unknown = this.instance?.get_property("$user_id")
    if (
      typeof identifiedEmail === "string" &&
      !isSameEmail(identifiedEmail, data.email)
    )
      this.reset()

    if (this.instance && data.email && this.shouldTrack(data)) {
      const { email, company, userType } = data
      const eventProperties = {
        slug: toLowerCase(company),
        user_type: userType
      }

      this.instance.identify(email)
      this.instance.people.set({ $email: email, $name: email })
      // A list, since one email can sign in both ways.
      this.instance.people.union({ user_types: [userType] })
      // Registered too, so Mixpanel's own heatmap events carry them; reset() clears them.
      this.instance.register(eventProperties)
      this.instance.start_session_recording()
      this.setDefaultMetaAttributes(eventProperties)

      this.identified = true
      this.requestIP?.then((ip: string | null) => {
        if (ip && this.instance) this.instance.people.set({ $ip: ip })
      })
    }
  }

  public reset() {
    if (this.instance) {
      this.stopSessionRecording()
      this.identified = false
      this.defaultMetaAttributes = {}
      this.instance.reset()
    }
  }

  public log(info: Services.LoggerAdapter.Info) {
    const { message, meta } = info
    if (this.identified && typeof message === "string") {
      this.instance?.track(message, { ...this.defaultMetaAttributes, ...meta })
    }
  }

  private shouldTrack(data: Services.LoggerAdapter.IdentificationData) {
    return isExternalUser(data.email)
  }
}

export { MixpanelTransport }
